import { db, functions, httpsCallable } from '@util/firebase';
import {
  CollectionReference,
  DocumentReference,
  arrayUnion,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  or,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { removeUndefinedRecursive } from '../products';
import { userRef } from '../users';
import {
  EbayCategoryMapping,
  EbayCategoryMapRow,
  EbayListingGroup,
  EbayOrderDoc,
  EbayTask,
  IntegrationDocument,
  IntegrationSyncError,
  IntegrationTypes,
  TaskDoc,
  UserIntegration,
} from './integrations.types';

type args = { type: IntegrationTypes; uid?: string };

export const colRef = collection(
  db,
  'integrations'
) as CollectionReference<IntegrationDocument>;

export async function getIntegrationList() {
  const { docs } = await getDocs(colRef);
  return docs.map((doc) => doc.data());
}

export async function getIntegrationById(id: string) {
  const snap = await getDoc(doc(colRef, id));
  return snap.data() ?? null;
}

export async function getIntegrationRequestsByUid(uid: string) {
  const col = collection(db, 'integration_requests');
  const q = query(col, where('uid', '==', uid));
  const { docs } = await getDocs(q);
  return docs.map((doc) => doc.data());
}

export async function getShopifyIntegrationByUsername(username: string) {
  const c = collection(db, 'integrations');
  const q = query(c, where('username', '==', username));
  const { docs } = await getDocs(q);
  return docs[0]?.data() ?? null;
}

export async function getUserIntegrations(uid: string) {
  if (!uid) return null;
  const col = collection(userRef, uid, 'integrations');
  const { docs } = await getDocs(col);
  return docs.map((doc) => doc.data() as UserIntegration);
}

export async function getAllUserIntegrations(type: IntegrationTypes) {
  if (!type) return null;
  const ref = collectionGroup(db, 'integrations');
  const q = query(ref, where('type', '==', type));
  const { docs } = await getDocs(q);
  return docs.map((doc) => doc.data() as UserIntegration);
}

export async function createUserIntegration(
  uid: string,
  type: IntegrationTypes,
  data: Omit<UserIntegration, 'settings' | 'cat_mapper'>
) {
  const ref = doc(userRef, uid, 'integrations', type);
  return setDoc(ref, data as any); // typescript thinks i need to send a userdoc here
}

export async function createIntegrationAdmin({
  uid,
  username,
  client_id,
  client_secret,
  store_domain,
  install_link,
  type,
}: {
  uid: string;
  username: string;
  client_id: string;
  client_secret: string;
  store_domain: string;
  install_link: string;
  type: IntegrationTypes;
}) {
  const ref = doc(userRef, uid, 'integrations', type);
  await setDoc(ref, {
    uid,
    username,
    client_id,
    client_secret,
    store_domain,
    install_link,
    type,
    cat_map_type: 'auto',
  } as any);
  // update user doc to have role of integrations
  const ref2 = doc(userRef, uid);
  await updateDoc(ref2, { 'roles.integrations': true });
}

export function getUserIntegrationQuery(uid: string, type: IntegrationTypes) {
  if (!uid) return null;
  const ref = doc(db, 'users', uid, 'integrations', type);
  return ref as DocumentReference<UserIntegration>;
}

export function getUserIntegrationTaskQuery(uid: string) {
  if (!uid) return null;
  const ref = doc(db, 'ebay_tasks', uid);
  return ref as DocumentReference<TaskDoc>;
}

export async function getIntegrationTaskOnce(uid: string) {
  const q = getUserIntegrationTaskQuery(uid);
  if (!q) return null;
  const snap = await getDoc(q);
  return snap.data() as TaskDoc;
}

export function getUserIntegrationErrorQuery({
  uid,
  type,
}: {
  uid: string;
  type: IntegrationTypes;
}) {
  if (!uid) return null;
  const colRef = collection(db, 'integration_errors');
  const q = query(colRef, where('uid', '==', uid), where('type', '==', type));
  return q;
}

export async function getUserIntegration(uid: string, type: IntegrationTypes) {
  if (!uid) return null;
  const ref = doc(db, 'users', uid, 'integrations', type);
  const snap = await getDoc(ref);
  return (snap.data() as UserIntegration) ?? null;
}

export async function setUserIntegrationByKey(
  uid: string,
  type: IntegrationTypes,
  key: keyof UserIntegration,
  value: any
) {
  const val = removeUndefinedRecursive(value);
  const ref = doc(db, 'users', uid, 'integrations', type);
  return updateDoc(ref, { [key]: val });
}

export async function updateUserIntegration(
  uid: string,
  type: IntegrationTypes,
  // allow fields or fieldtypes (deleteField, addField, etc)
  data: Partial<UserIntegration> | { [key: string]: any }
) {
  const ref = doc(db, 'users', uid, 'integrations', type);
  return updateDoc(ref, data);
}

export async function initialSetup(type: IntegrationTypes, code: string) {
  const { data } = await httpsCallable<
    args & { code: string },
    { account_name: string }
  >(
    functions,
    'initialSetup'
  )({ type, code });
  return data;
}

export async function getCategories(type: IntegrationTypes) {
  const { data } = await httpsCallable<args, string[]>(
    functions,
    'integrations-v2_getShopifyCategories'
  )({ type });
  return data;
}

export async function getCollections(type: IntegrationTypes) {
  try {
    const { data } = await httpsCallable<args, any[]>(
      functions,
      'integrations-v2_getShopifyCollections'
    )({ type });
    return data as { id: number; title: string }[];
  } catch (e) {
    alert(e);
    return [];
  }
}

export async function initialInventorySync(type: IntegrationTypes) {
  await httpsCallable<args, { imported: number; total: number }>(
    functions,
    'integrations-v2_enqueueFullSync',
    { timeout: 10 * 60 * 1000 } // 10 minutes
  )({ type });
  return;
}

export async function deleteInventoryFromSync(
  type: IntegrationTypes,
  uid?: string
) {
  const { data } = await httpsCallable<args, { deleted: number }>(
    functions,
    'integrations-v2_deleteInventoryFromSync'
  )({ type, uid });
  return data;
}

export async function removeIntegration({
  uid,
  type,
}: {
  uid: string;
  type: IntegrationTypes;
}) {
  const { data } = await httpsCallable<args, void>(
    functions,
    'integrations-v2_removeIntegration'
  )({ type, uid });
  return data;
}

export async function setupWebhooks() {
  // v2_setupWebhooks
  await httpsCallable<args, void>(
    functions,
    'integrations-v2_setupWebhooks'
  )({
    type: 'shopify',
  });
}
export async function getIntegrationErrors({
  type,
  uid,
}: {
  type: IntegrationTypes;
  uid?: string;
}) {
  if (!uid) return [];
  const col = collection(db, 'integration_errors');
  const q = query(col, where('uid', '==', uid), where('type', '==', type));
  const { docs } = await getDocs(q);
  return docs.map((doc) => doc.data()) as IntegrationSyncError[];
}

export async function getIntegrationErrorCount({
  type,
  uid,
}: {
  type: IntegrationTypes;
  uid?: string;
}) {
  if (!uid) return 0;
  const col = collection(db, 'integration_errors');
  const q = query(
    col,
    where('uid', '==', uid),
    where('type', '==', type),
    where(
      'error_type',
      '!=',
      type === 'ebay'
        ? 'EBAY_PRODUCT_NOT_ALLOWED'
        : 'SHOPIFY_PRODUCT_NOT_ALLOWED'
    )
  );
  const res = await getCountFromServer(q);
  return res.data().count;
}

export async function getIntegrationErrorByProductId(
  productId: string,
  sellerId: string
) {
  const col = collection(db, 'integration_errors');
  const q = query(
    col,
    where('uid', '==', sellerId),
    where('existing_product_id', '==', productId),
    limit(1)
  );
  const { docs } = await getDocs(q);
  return docs[0]?.data() as IntegrationSyncError;
}

export async function getIntegrationProductCount({
  type,
  uid,
}: {
  type: IntegrationTypes;
  uid?: string;
}) {
  if (!uid) return 0;
  const col = collection(db, 'products');
  const q = query(
    col,
    where('integration_info.type', '==', type),
    where('seller_id', '==', uid),
    where('out_of_stock', '==', false)
  );

  const res = await getCountFromServer(q);
  return res.data().count;
}

export async function deleteProductsFromSync({
  ids,
}: {
  ids: string[];
}): Promise<void> {
  await httpsCallable<{ ids: string[] }, void>(
    functions,
    'integrations-v2_deleteProductsFromSync'
  )({ ids });
}

export async function deleteIntegrationError(id: string) {
  const ref = doc(db, 'integration_errors', id);
  // delete the error
  await deleteDoc(ref);
}

export async function deleteAllIntegrationErrors({
  uid,
  type,
}: {
  uid?: string;
  type: IntegrationTypes;
}) {
  if (!uid) throw new Error('No authentication found');
  const col = collection(db, 'integration_errors');
  const q = query(col, where('uid', '==', uid), where('type', '==', type));
  let count = await getCountFromServer(q);
  while (count.data().count) {
    const qWithLimit = query(q, limit(500));
    const { docs } = await getDocs(qWithLimit);
    const batch = writeBatch(db);
    docs.forEach((doc) => batch.delete(doc.ref));
    await batch.commit();
    count = await getCountFromServer(q);
  }
  const ref = doc(db, 'users', uid, 'integrations', type);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, {
      current_sync_error_count: 0,
    });
  }
}
export async function createIntegrationRequest({
  email,
  store_domain,
  type,
  uid,
}: {
  email: string;
  store_domain: string;
  type: IntegrationTypes;
  uid?: string;
}) {
  const ref = doc(collection(db, 'integration_requests'));
  await setDoc(ref, { type, email, store_domain, ...(uid && { uid }) });
}

export async function getIntegrationRequests(type: IntegrationTypes) {
  const col = collection(db, 'integration_requests');
  const q = query(col, where('type', '==', type));
  const { docs } = await getDocs(q);
  return docs.map((doc) => ({
    ...(doc.data() as {
      email: string;
      store_domain: string;
      type: IntegrationTypes;
      uid?: string;
    }),
    id: doc.id,
  }));
}

export async function deleteIntegrationRequest(id: string) {
  const ref = doc(db, 'integration_requests', id);
  await deleteDoc(ref);
}

export async function getIntegrationRequestsCount() {
  const col = collection(db, 'integration_requests');
  const res = await getCountFromServer(col);
  return res.data().count;
}

export async function retryCategorization(product_id: string) {
  await httpsCallable<{ product_id: string }>(
    functions,
    'integrations-v2_retryCategorySync'
  )({ product_id });
}

// Ebay

export async function getStoppedEbayTasks(): Promise<
  ({ uid: string } & TaskDoc)[]
> {
  const col = collection(db, 'ebay_tasks');
  const q = query(
    col,
    or(where('stop', '==', true), where('global_stop', '==', true))
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => ({ uid: d.id, ...(d.data() as TaskDoc) }));
}

export async function ebayFullSync(uid?: string) {
  if (!uid) throw new Error('No authentication found');
  const colRef = collection(db, 'ebay_tasks');
  const taskRef = doc(colRef, uid);
  const taskSnap = await getDoc(taskRef);
  const task: EbayTask = {
    task_type: 'GetSellerList',
    task_data: {
      page_number: 1,
    },
    date_created: Date.now(), // TODO: confirm this is the same as the server
  };
  if (!taskSnap.exists()) {
    const newRef = doc(colRef, uid);
    await setDoc(newRef, {
      stop: false,
      tasks: [task],
    });
  } else {
    // Don't set stop false because the backend might be running a task
    await setDoc(
      taskRef,
      {
        tasks: arrayUnion(task),
      },
      { merge: true }
    );
  }
}

export async function ebayToggleSync(uid?: string) {
  if (!uid) throw new Error('No authentication found');
  const colRef = collection(db, 'ebay_tasks');
  const taskRef = doc(colRef, uid);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) return;
  const task = taskSnap.data() as TaskDoc;
  await updateDoc(taskRef, {
    global_stop: !task.global_stop,
  });
  return !task.global_stop;
}

export async function ebaySyncOne({
  remote_id,
  uid,
  product_id,
}: {
  remote_id: string;
  uid?: string;
  product_id?: string;
}) {
  if (!uid) throw new Error('No authentication found');
  if (!remote_id) throw new Error('Please provide an eBay item ID');
  const newTask: EbayTask = {
    task_type: 'GetItem',
    task_data: {
      item_id: remote_id,
      ...(product_id && { mx_product_id: product_id }),
    },
    date_created: 0, // prioritize this task over all others
  };
  const colRef = collection(db, 'ebay_tasks');
  const taskRef = doc(colRef, uid);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) {
    await setDoc<TaskDoc, TaskDoc>(taskRef, {
      stop: false,
      tasks: arrayUnion(newTask),
    });
  } else {
    await updateDoc<TaskDoc, TaskDoc>(taskRef, {
      tasks: arrayUnion(newTask),
    });
  }
}

export async function scheduleEbaySync({
  remote_ids,
  uid,
  delay,
}: {
  remote_ids: string[];
  uid?: string;
  delay: number;
}) {
  if (!uid) throw new Error('No authentication found');
  if (!remote_ids.length)
    throw new Error('Please provide at least one ebay item id');
  await httpsCallable(
    functions,
    'integrations-ebay-v2_scheduleTask'
  )({
    remote_ids,
    uid,
    delay,
  });
}

export async function completeAuthentication(code: string) {
  const res = await httpsCallable<{ code: string }, void>(
    functions,
    'integrations-ebay-v2_completeAuthentication'
  )({ code });
  return res.data;
}

// Rithium (ChannelAdvisor)
// Grouped export → full deployed name is `integrations-rithium-v2_*`.

export async function getRithiumAuthUrl() {
  const res = await httpsCallable<Record<string, never>, { url: string }>(
    functions,
    'integrations-rithium-v2_getRithiumAuthUrl'
  )({});
  return res.data;
}

export async function completeRithiumAuth(code: string) {
  const res = await httpsCallable<{ code: string }, { success: boolean }>(
    functions,
    'integrations-rithium-v2_completeRithiumAuth'
  )({ code });
  return res.data;
}

// Best-effort sampled list of distinct ChannelAdvisor label names. ChannelAdvisor
// has no endpoint to enumerate all labels, so this is NOT guaranteed exhaustive —
// the UI must still allow free-text entry of an exact label name (MXWEB-3856).
export async function getRithiumLabels() {
  const res = await httpsCallable<
    Record<string, never>,
    { labels: string[] } | string[]
  >(
    functions,
    'integrations-rithium-v2_getRithiumLabels'
  )({});
  // Normalize either shape to a de-duped string array.
  const labels = Array.isArray(res.data) ? res.data : res.data?.labels ?? [];
  return Array.from(new Set(labels.filter(Boolean)));
}

// Persists `sync_label` on users/{uid}/integrations/rithium. The full sync is
// guarded server-side and refuses to run until this is set (avoids importing the
// seller's entire catalog).
export async function setRithiumSyncLabel(label: string) {
  const res = await httpsCallable<{ label: string }, { success: boolean }>(
    functions,
    'integrations-rithium-v2_setRithiumSyncLabel'
  )({ label });
  return res.data;
}

export async function enqueueRithiumFullSync() {
  const res = await httpsCallable<Record<string, never>, { success: boolean }>(
    functions,
    'integrations-rithium-v2_enqueueRithiumFullSync',
    { timeout: 10 * 60 * 1000 } // 10 minutes — mirrors the Shopify full-sync call
  )({});
  return res.data;
}

export async function getSessionID() {
  const res = await httpsCallable<
    void,
    {
      sessionID: string;
      ruName: string;
    }
  >(functions, 'integrations-ebay-v2_getSessionID')();
  return res.data;
}

export async function fetchToken({
  sessionID,
  username,
}: {
  sessionID: string;
  username: string;
}) {
  const res = await httpsCallable<
    {
      sessionID: string;
      username: string;
    },
    void
  >(
    functions,
    'integrations-ebay-v2_fetchToken'
  )({ sessionID, username });
  return res.data;
}

export async function ebayDeleteListings(uid?: string, drafts_only?: boolean) {
  if (!uid) throw new Error('No authentication found');
  const colRef = collection(db, 'ebay_tasks');
  const taskRef = doc(colRef, uid);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) return;
  // Pause the task queue
  await updateDoc(taskRef, {
    global_stop: true,
  });
  await httpsCallable<{ drafts_only?: boolean }, void>(
    functions,
    'integrations-ebay-v2_deleteListings'
  )({ drafts_only });
}

export async function retryEbayTask(uid: string) {
  const taskRef = doc(db, 'ebay_tasks', uid);
  await updateDoc(taskRef, { stop: false, failed: false });
}

export async function clearAllTasks(uid?: string) {
  if (!uid) return;
  const taskRef = doc(db, 'ebay_tasks', uid);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) return;
  await updateDoc(taskRef, {
    tasks: [],
    stop: false,
    global_stop: true,
  });
}

export async function getMissingCategories(uid?: string) {
  const colRef = collection(db, 'integration_errors');
  const q = query(
    colRef,
    where('uid', '==', uid),
    where('error_type', '==', 'MISSING_MAPPING_FROM_MX'),
    limit(1000)
  );
  const { docs } = await getDocs(q);
  const errors = docs.map((doc) => doc.data()) as IntegrationSyncError[];
  return groupErrorsByCategoryId(errors);
}

// The converter writes 'Error converting ebay item 156547509067 to mx product -
// No category found 179740', so the trailing token is the unmapped eBay
// category ID. Groups the eBay item IDs under it: { '179740': ['156547509067'] }
const MISSING_CATEGORY_DELIMITER = ' to mx product - No category found ';

function groupErrorsByCategoryId(errors: IntegrationSyncError[]) {
  return errors.reduce((acc, error) => {
    const categoryId = error.user_message
      .split(MISSING_CATEGORY_DELIMITER)[1]
      ?.trim();
    if (!categoryId) return acc;
    if (!acc[categoryId]) acc[categoryId] = [];
    acc[categoryId].push(error.remote_id);
    return acc;
  }, {} as { [key: string]: string[] });
}

// Same grouping across every seller, so the Category Mapper can show which
// eBay categories are actually costing us listings right now.
export async function getUnmappedEbayCategories() {
  const { docs } = await getDocs(
    query(
      collection(db, 'integration_errors'),
      where('error_type', '==', 'MISSING_MAPPING_FROM_MX'),
      where('type', '==', 'ebay'),
      limit(2000)
    )
  );
  const grouped = groupErrorsByCategoryId(
    docs.map((doc) => doc.data()) as IntegrationSyncError[]
  );
  return Object.entries(grouped)
    .map(([ebay_category_id, remote_ids]) => ({
      ebay_category_id,
      listing_count: remote_ids.length,
    }))
    .sort((a, b) => b.listing_count - a.listing_count);
}

// integration_settings/ebay_category_map holds every admin-configured eBay
// category mapping in one `map` field keyed by eBay category ID. The eBay
// converter reads it and falls back to its in-code map for IDs that are absent,
// so an entry here is always an override or an addition.
const ebayCategoryMapRef = () =>
  doc(db, 'integration_settings', 'ebay_category_map');

export async function listEbayCategoryMappings(): Promise<
  EbayCategoryMapRow[]
> {
  const snap = await getDoc(ebayCategoryMapRef());
  const map = (snap.data()?.map ?? {}) as Record<string, EbayCategoryMapping>;
  return Object.entries(map)
    .map(([ebay_category_id, mapping]) => ({
      ebay_category_id,
      ride_types: mapping.ride_types ?? [],
      cats: mapping.cats ?? [],
    }))
    .sort((a, b) => Number(a.ebay_category_id) - Number(b.ebay_category_id));
}

// setDoc with merge rather than updateDoc so the first mapping ever saved
// creates the doc, and so two admins editing different IDs cannot clobber each
// other's key.
export async function setEbayCategoryMapping(args: EbayCategoryMapRow) {
  const { ebay_category_id, ride_types, cats } = args;
  await setDoc(
    ebayCategoryMapRef(),
    { map: { [ebay_category_id]: { ride_types, cats } } },
    { merge: true }
  );
}

// Removing the override falls the ID back to the eBay converter's in-code map,
// if that map has an entry for it.
export async function deleteEbayCategoryMapping(ebay_category_id: string) {
  await updateDoc(ebayCategoryMapRef(), {
    [`map.${ebay_category_id}`]: deleteField(),
  });
}

export async function addWPSItem(args: { sku: string; product_id?: string }) {
  if (!args.sku?.trim()) throw new Error('Please provide a SKU');
  args.sku = args.sku.trim();
  const res = await httpsCallable<
    { sku: string; product_id?: string },
    {
      id: string;
      stock: number;
      type: 'create' | 'update';
      url: string;
    }
  >(
    functions,
    'integrations-wps-v2_addWPSItem'
  )(args);
  return res.data;
}

export async function addHHItem(args: { sku: string }) {
  if (!args.sku?.trim()) throw new Error('Please provide a SKU');
  args.sku = args.sku.trim();
  const res = await httpsCallable<
    { sku: string },
    {
      id: string;
      stock: number;
      type: 'create' | 'update';
      url: string;
    }
  >(
    functions,
    'integrations-hh-v2_addHHItem'
  )(args);
  return res.data;
}

export async function addPUItem(args: { sku: string }) {
  if (!args.sku?.trim()) throw new Error('Please provide a SKU');
  args.sku = args.sku.trim();
  const res = await httpsCallable<{ sku: string }, { slug: string | null }>(
    functions,
    'integrations-pu-v2_addPUItem'
  )(args);
  return res.data;
}

export async function ungroupWarehouseGroup(args: { warehouse_id: string }) {
  if (!args.warehouse_id?.trim())
    throw new Error('Please provide a warehouse ID');
  const res = await httpsCallable<
    { warehouse_id: string },
    { new_warehouse_entries: Array<{ id: string; mpn: string[] }> }
  >(
    functions,
    'integrations-warehouse-v2_ungroupWarehouseGroup'
  )(args);
  return res.data;
}

export type EnrichmentFields = {
  categories: boolean;
  ride_types: boolean;
  fitment: boolean;
  brands: boolean;
};
export async function initiateEnrichment(args: {
  seller_id: string;
  fields: EnrichmentFields;
}) {
  const { data } = await httpsCallable<
    {
      seller_id: string;
      fields: EnrichmentFields;
    },
    { success: true } | { success: false; error: string }
  >(
    functions,
    'ai-requests-v2_initiateEnrichment'
  )(args);
  return data;
}

// eBay outbound export (MXWEB-3874). Admin-only callables on the grouped export
// → full deployed names take the `integrations-ebay-` prefix.

export type EbayEnrollArgs = {
  seller_id?: string;
  product_ids?: string[];
  // Enrolls exactly these listings, skipping the catalog scan entirely. This is
  // what the search-and-select panel sends, so what gets published is what was
  // previewed rather than whatever a re-run of the scan now resolves to.
  listing_keys?: string[];
  scan_limit?: number;
  listing_cap?: number;
  // Rejects the enrollment when the selected listing value exceeds what is left
  // of the seller's monthly limit.
  value_cap?: number;
  accept?: boolean;
};

// The listing/product split, shared by the dry run and the accepted run.
export type EbayEnrollSummary = {
  products_scanned: number;
  listings_available: number;
  listings_selected: number;
  products_covered: number;
  variation_listings: number;
  single_listings: number;
  listings_skipped: number;
  skip_reasons: Record<string, number>;
  // Σ price x listed qty over the selected listings. Optional so the UI still
  // renders against a deploy that predates the value tracking.
  selected_value?: number;
};

export type EbayEnrollDryRun = EbayEnrollSummary & {
  dry_run: true;
  sample: {
    listing_key: string;
    kind: string;
    members: number;
    reason?: string;
  }[];
};

export type EbayEnrollAccepted = EbayEnrollSummary & {
  dry_run: false;
  enqueued: number;
};

export type EbayEnrollResult = EbayEnrollDryRun | EbayEnrollAccepted;

// Without `accept` this only plans and returns the split; with it, every
// selected listing is enqueued for a real publish against the live eBay
// account. Always dry-run first.
export async function enrollEbayExport(args: EbayEnrollArgs) {
  const res = await httpsCallable<EbayEnrollArgs, EbayEnrollResult>(
    functions,
    'integrations-ebay-v2_enrollEbayExport',
    { timeout: 9 * 60 * 1000 } // the callable itself runs up to 540s
  )(args);
  return res.data;
}

// One resolved eBay listing. Picking a product can pull in its whole group, and
// an ungroupable group fans out into one solo listing per member, so a selection
// has to be resolved server-side before it means anything.
export type EbayListingPreview = {
  listing_key: string;
  kind: 'single' | 'variation' | 'flat' | 'skip';
  members: number;
  // Σ price x listed qty over members — what eBay bills against the limit.
  listing_value: number;
  export_status?: EbayListingGroup['status'];
  skip_reason?: string;
  // Which of the selected products resolved into this listing.
  product_ids: string[];
};

export type EbayPreviewResult = {
  listings: EbayListingPreview[];
  products_requested: number;
  total_value: number;
  /** Populated when `warehouse_ids` were asked for. */
  warehouses: WarehouseListingRow[];
  /** Ids with no warehouse doc and no products — almost always a typo. */
  missing_warehouse_ids: string[];
};

// Costs a selection before it is enrolled, in whichever unit the UI selected
// in: products, or the warehouse groups the page actually works in. No cap
// slicing — the UI owns selection.
export async function previewEbayListings(args: {
  seller_id?: string;
  product_ids?: string[];
  warehouse_ids?: string[];
}) {
  const res = await httpsCallable<typeof args, EbayPreviewResult>(
    functions,
    'integrations-ebay-v2_previewEbayListings',
    { timeout: 9 * 60 * 1000 }
  )(args);
  return res.data;
}

// The monthly selling limit and the listed value already against it. Sold items
// count toward the limit too, so this is an estimate of exposure rather than a
// ledger — Seller Hub stays the source of truth.
export type EbayExportBudget = {
  seller_id: string;
  limit: number;
  listed_value: number;
  remaining: number;
  listing_count: number;
};

export async function getEbayExportBudget(args: { seller_id?: string } = {}) {
  const res = await httpsCallable<typeof args, EbayExportBudget>(
    functions,
    'integrations-ebay-v2_getEbayExportBudget'
  )(args);
  return res.data;
}

export async function getEbayOrderInfo(args: {
  ebay_order_id?: string;
  order_num?: number;
}) {
  const res = await httpsCallable<typeof args, EbayOrderDoc>(
    functions,
    'integrations-ebay-v2_getEbayOrderInfo'
  )(args);
  return res.data;
}

type EbayOutboundConnected = {
  seller_id: string;
  missing: string[];
  merchant_location_key: string | null;
};

export type EbayOutboundSetup =
  | { step: 'awaiting_oauth'; consent_url: string; seller_id: string }
  | ({ step: 'awaiting_policies' } & EbayOutboundConnected)
  | ({ step: 'ready' } & EbayOutboundConnected);

// Idempotent: with no code it reports the connection state (and the consent
// URL when the refresh token is gone); with a code it reconnects the account.
export async function setupEbayOutbound(args: { code?: string } = {}) {
  const res = await httpsCallable<typeof args, EbayOutboundSetup>(
    functions,
    'integrations-ebay-v2_setupEbayOutbound'
  )(args);
  return res.data;
}

// Every listing group for a seller. The export queue does not write anything
// back to the product docs, so these are the only record of what is enrolled.
export async function getEbayListingGroupsBySeller(seller_id: string) {
  const snap = await getDocs(
    query(
      collection(db, 'ebay_listing_groups'),
      where('seller_id', '==', seller_id)
    )
  );
  return snap.docs.map((d) => d.data() as EbayListingGroup);
}

export async function getEbayListingGroup(listing_key: string) {
  const ref = doc(
    collection(db, 'ebay_listing_groups'),
    listing_key
  ) as DocumentReference<EbayListingGroup>;
  const snap = await getDoc(ref);
  return snap.data() ?? null;
}

// One warehouse group priced as the eBay listing it becomes. Products built
// from a warehouse doc carry `group_id = warehouse_id`, so a warehouse id is
// one listing key — except a `flat` group, which fans out into one listing per
// member because it can't form a valid variation listing.
export type WarehouseListingRow = {
  warehouse_id: string;
  listing_key: string;
  title: string;
  brand: string;
  image?: string;
  mpns: string[];
  skus: string[];
  product_count: number;
  in_stock_count: number;
  kind: 'single' | 'variation' | 'flat' | 'skip' | 'out_of_stock';
  members: number;
  listing_count: number;
  listing_value: number;
  export_status?: EbayListingGroup['status'];
  listing_id?: string;
  skip_reason?: string;
};

export type EbayWithdrawSummary = {
  seller_id: string;
  listings_targeted: number;
  /** Of those, the ones actually live on eBay and needing an API call. */
  listings_live: number;
  products_covered: number;
  listed_value: number;
};

export type EbayWithdrawResult =
  | (EbayWithdrawSummary & { dry_run: true })
  | (EbayWithdrawSummary & { dry_run: false; enqueued: number });

// Takes listings back off eBay. Dry-run without `accept`. Ending a listing
// means a fresh insertion fee to put it back, so this is never implicit.
export async function withdrawEbayListings(args: {
  seller_id?: string;
  listing_keys?: string[];
  all?: boolean;
  accept?: boolean;
}) {
  const res = await httpsCallable<typeof args, EbayWithdrawResult>(
    functions,
    'integrations-ebay-v2_withdrawEbayListings',
    { timeout: 9 * 60 * 1000 }
  )(args);
  return res.data;
}
